'use client';

import { useEffect, useRef, useState } from 'react';
import { isDev } from '~/lib/utils';

interface IntersectionObserverOptions {
  onIntersect: () => void;
  isDisabled?: boolean;
  rootMargin?: string;
  threshold?: number;
}

/**
 * A simplified hook for intersection observation that can be used
 * independently of SWR for general intersection detection
 */
export const useIntersectionObserver = ({
  onIntersect,
  isDisabled = false,
  rootMargin = '300%',
  threshold = 0.01,
}: IntersectionObserverOptions) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const [lastElement, setLastElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // If disabled, don't set up the observer
    if (isDisabled) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first && first.isIntersecting) {
          if (isDev) console.log('Element intersected, triggering callback');
          onIntersect();
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    return () => {
      observer.current?.disconnect();
    };
  }, [onIntersect, isDisabled, rootMargin, threshold]);

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement && currentObserver && !isDisabled) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement && currentObserver) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement, isDisabled]);

  return { setLastElement };
};
